/**
 * <MatchedPairs /> — renders the "Mutual Gratitude" section at the top of a
 * gratitude wall (both the logged-in user's own wall at /tools/gratitude and
 * any public `/:username/gratitude` page).
 *
 * Behaviour:
 *   - Row of compact tiles, each showing both participants' avatars side by
 *     side, a gold "Matched" ribbon, and the per-direction counts.
 *   - Tap a tile → expands inline to reveal the two most-recent public cards
 *     (`from_me` + `from_them`) with a soft gold connector between them.
 *   - Framer-Motion handles the fade-in on mount and the side-by-side slide
 *     when a tile is expanded.
 *
 * If `matches` is empty or undefined the whole section renders nothing.
 */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowLeftRight, X } from "lucide-react";
import GratitudeCard from "./GratitudeCard";

const goldBg =
  "linear-gradient(135deg, #D4A645 0%, #E8C677 50%, #B88A2E 100%)";

export default function MatchedPairs({ matches, viewerName = "You", wheelAreaMap = {}, readOnly = false }) {
  const [expandedId, setExpandedId] = useState(null);

  if (!matches || matches.length === 0) return null;

  return (
    <section className="mb-8" data-testid="matched-pairs">
      <header className="flex items-baseline justify-between mb-3">
        <div>
          <h3
            className="text-sm font-semibold inline-flex items-center gap-1.5"
            style={{
              color: "var(--color-text-primary)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
            data-testid="matched-pairs-heading"
          >
            <Sparkles size={14} style={{ color: "#B88A2E" }} />
            Mutual Gratitude
          </h3>
          <p
            className="text-xs mt-1"
            style={{ color: "var(--color-text-secondary)" }}
          >
            Pairs where {viewerName === "You" ? "you've both added each other" : `${viewerName} and they have added each other`} to your walls.
          </p>
        </div>
        <span
          className="text-xs font-semibold px-2 py-0.5 rounded-full"
          style={{ background: "rgba(184, 138, 46, 0.1)", color: "#8B6A1F" }}
        >
          {matches.length} {matches.length === 1 ? "pair" : "pairs"}
        </span>
      </header>

      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}
      >
        {matches.map((m, i) => (
          <MatchTile
            key={m.match_id}
            match={m}
            viewerName={viewerName}
            delay={i * 0.06}
            expanded={expandedId === m.match_id}
            onToggle={() =>
              setExpandedId(expandedId === m.match_id ? null : m.match_id)
            }
            wheelAreaMap={wheelAreaMap}
            readOnly={readOnly}
          />
        ))}
      </div>
    </section>
  );
}

function MatchTile({ match, viewerName, delay, expanded, onToggle, wheelAreaMap, readOnly }) {
  const partner = match.partner || {};
  const firstOfViewer = viewerName === "You" ? "You" : (viewerName.split(" ")[0] || "You");
  const firstOfPartner = (partner.name || "them").split(" ")[0];
  const total = (match.my_count || 0) + (match.their_count || 0);

  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay }}
      className="relative"
      style={{
        gridColumn: expanded ? "1 / -1" : "auto",
      }}
      data-testid={`match-tile-${match.match_id}`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left relative overflow-hidden rounded-xl transition-shadow hover:shadow-md"
        style={{
          background: "var(--color-surface)",
          border: "1px solid rgba(184, 138, 46, 0.35)",
          padding: "14px 16px 12px",
        }}
        aria-expanded={expanded}
        data-testid={`match-tile-btn-${match.match_id}`}
      >
        {/* Gold "Mutual" ribbon — tiny chip in the top-right corner */}
        <div
          className="absolute top-0 right-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-bl-lg"
          style={{ background: goldBg, color: "#fff", letterSpacing: "0.1em" }}
        >
          Mutual
        </div>

        <div className="flex items-center gap-3 mb-3">
          <Avatars viewer={{ name: viewerName, picture: match.from_me?.picture }} partner={partner} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>
              {firstOfViewer} & {firstOfPartner}
            </div>
            {partner.username && (
              <div className="text-xs truncate" style={{ color: "var(--color-text-secondary)" }}>
                @{partner.username}
              </div>
            )}
          </div>
        </div>

        <div
          className="text-xs flex items-center gap-2"
          style={{ color: "var(--color-text-secondary)" }}
        >
          <ArrowLeftRight size={12} />
          <span data-testid={`match-count-${match.match_id}`}>
            {match.my_count} ↗ · {match.their_count} ↙ · {total} total
          </span>
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28 }}
            className="mt-3"
            data-testid={`match-expand-${match.match_id}`}
          >
            <div className="flex justify-end mb-2">
              <button
                onClick={onToggle}
                className="inline-flex items-center gap-1 text-xs hover:opacity-80"
                style={{ color: "var(--color-text-secondary)" }}
                aria-label="Collapse"
                data-testid={`match-collapse-${match.match_id}`}
              >
                <X size={12} /> Close
              </button>
            </div>
            <div className="relative grid gap-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <CardOrPlaceholder
                card={match.from_me}
                label={`From ${firstOfViewer.toLowerCase() === "you" ? "you" : firstOfViewer}`}
                wheelAreaMap={wheelAreaMap}
                readOnly={readOnly}
              />
              {/* Soft gold connector between the two cards */}
              <div
                aria-hidden
                className="absolute top-1/2 -translate-y-1/2 rounded-full hidden md:block"
                style={{
                  left: "calc(50% - 18px)",
                  width: 36,
                  height: 2,
                  background: goldBg,
                }}
              />
              <CardOrPlaceholder
                card={match.from_them}
                label={`From ${firstOfPartner}`}
                wheelAreaMap={wheelAreaMap}
                readOnly={readOnly}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}

function Avatars({ viewer, partner }) {
  return (
    <div className="flex items-center">
      <Avatar url={viewer.picture} name={viewer.name} ring />
      <div style={{ marginLeft: -12, zIndex: 1 }}>
        <Avatar url={partner.picture} name={partner.name} ring />
      </div>
    </div>
  );
}

function Avatar({ url, name, ring }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  const ringStyle = ring
    ? { boxShadow: "0 0 0 2px var(--color-surface)" }
    : {};
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className="w-9 h-9 rounded-full object-cover"
        style={ringStyle}
      />
    );
  }
  return (
    <div
      className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold"
      style={{
        background: "var(--color-brand-ghost)",
        color: "var(--color-brand-primary)",
        ...ringStyle,
      }}
    >
      {initial}
    </div>
  );
}

function CardOrPlaceholder({ card, label, wheelAreaMap, readOnly }) {
  if (!card) {
    return (
      <div
        className="rounded-xl p-5 text-sm text-center"
        style={{
          border: "1px dashed var(--color-border-default)",
          background: "var(--color-surface-subtle)",
          color: "var(--color-text-placeholder)",
          minHeight: 140,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        No public card yet
      </div>
    );
  }
  return (
    <div>
      <div
        className="text-[10px] font-semibold mb-1.5 uppercase"
        style={{ color: "var(--color-text-secondary)", letterSpacing: "0.08em" }}
      >
        {label}
      </div>
      <GratitudeCard card={card} wheelAreaMap={wheelAreaMap} readOnly={readOnly} />
    </div>
  );
}
